import assign from 'object-assign';
import namespace from './namespace';
import {RegistrationSteps, RegistrationStepsMap, ShippingTierMap} from '@upi/constants';
import {googleAnalyticsLayer} from './data-layer-ga4';
import {facebookPixelLayer} from './data-layer-fbq';
import {
    applyProductSelectionAttribution,
    clearCartAttributions,
    enrichProductsWithCartAttribution,
    rememberCartAttributions,
    rememberProductSelection,
    removeProductSelections
} from './commerce-attribution-store';
import {
    getServerCartAdditionItems,
    parseConfirmedServerCartAdditions
} from './server-cart-additions';
import CartService from '@services/CartService';
import OrdersService from '@services/OrdersService';
import prop from 'ramda/src/prop';
import {
    CommerceItemLists,
    getCanonicalItemList,
    getCanonicalItemListByName,
    isCanonicalItemList,
    SearchTypes,
    SuggestionTypes
} from './data-layer-constants';

export {CommerceItemLists, SearchTypes, SuggestionTypes};

const dataLayer = namespace( 'dataLayer' );
const dataLayerProto = {
    async getCartData () {
        try {

            const cart = await CartService.getCart();
            const rawCartItems = cart.lineItems?.map( line => ({
                ...line.product,
                quantity: line.quantity,
                linePrice: line.linePrice,
                currencyCode: line.currencyCode,
                currencyCountry: line.currencyCountry
            }) ).filter( item => item != null && item.sku != null ) || [];
            if ( rawCartItems.length === 0 ) {
                clearCartAttributions( undefined, cart.orderGuid );
            }
            const cartItems = enrichProductsWithCartAttribution(
                rawCartItems,
                undefined,
                cart.orderGuid
            );

            return {
                cart,
                cartItems
            }
        }
        catch ( err ) {
            console.error( `[DataLayer]: Failed to fetch cart data; ${err.message}` );
        }
    },

    async getOrder ( orderNumber ) {
        try {
            const {
                subtotal,
                shippingTotal,
                tax,
                orderGuid,
                lineItems,
                promotions
            } = await OrdersService.getOrderById( {orderNumber} );

            return {
                orderNumber: orderNumber,
                shippingTotal,
                tax,
                subtotal,
                orderGuid,
                items: enrichProductsWithCartAttribution(
                    lineItems?.map( line => ( {
                        ...line.product,
                        quantity: line.quantity,
                        linePrice: line.linePrice,
                        currencyCode: line.currencyCode,
                        currencyCountry: line.currencyCountry
                    } ) ).filter( item => item != null && item.sku != null ) || [],
                    undefined,
                    orderGuid
                ),
                promoCode: promotions?.map( prop( 'code' ) ).toString()
            }
        }
        catch ( err ) {
            console.error( `[DataLayer]: Failed to fetch cart data; ${err.message}` );
        }
    }
};

dataLayer.create = ( obj ) => assign( {}, dataLayerProto, obj );

export const forms = dataLayer.create( {
    formSubmit ( formDetail ) {
        googleAnalyticsLayer.formSubmit( formDetail );
    }
} );

export const cart = dataLayer.create( {
    sendCrossSellItems ( list = [] ) {
        googleAnalyticsLayer.crossSellItems( list );
        return this;
    },

    sendItemsAdded ( items = [] ) {
        googleAnalyticsLayer.itemsAdded( items );
        facebookPixelLayer.itemsAdded( items );
        return this;
    },

    sendItemsRemoved ( items = [] ) {
        googleAnalyticsLayer.itemsRemoved( items );
        return this;
    }
} );

export const checkout = dataLayer.create( {
    async beginCheckout () {
        const {cartItems} = await this.getCartData();
        googleAnalyticsLayer.beginCheckout( cartItems );
    },

    async setPaymentOption ( paymentMethodType ) {
        const {cartItems} = await this.getCartData();
        googleAnalyticsLayer.addPaymentInfo( {paymentMethodType, items: cartItems} );
        return this;
    },

    async setShippingOption ( shippingMethod ) {
        const {cartItems} = await this.getCartData();
        googleAnalyticsLayer.addShippingInfo( {
            shippingMethod: ShippingTierMap[shippingMethod] || 'Standard Shipping (3 Business Days)',
            items: cartItems
        } );
        return this;
    },

    async purchaseComplete ( orderNumber ) {
        if ( !orderNumber || Number( orderNumber ) <= 0 ) {
            return;
        }

        const lastOrder = await this.getOrder( orderNumber );

        if ( lastOrder ) {
            googleAnalyticsLayer.purchaseComplete( lastOrder );
            facebookPixelLayer.purchaseComplete( lastOrder.items );
            clearCartAttributions( undefined, lastOrder.orderGuid );
        }
    }
} );

export const product = dataLayer.create( {
    sendProductView ( sku ) {
        googleAnalyticsLayer.productView( sku );
        facebookPixelLayer.productView( sku );
        return this;
    },
    sendProductClick ( item, context ) {
        googleAnalyticsLayer.productClick( item, context );
        const contextItemList = context?.itemList;
        const itemList = isCanonicalItemList( contextItemList )
            ? contextItemList
            : getCanonicalItemList( item?.itemListId || item?.item_list_id, item?.itemListName || item?.item_list_name )
                || getCanonicalItemListByName( item?.actionField );

        if ( itemList ) {
            rememberProductSelection( item, itemList );
        }
        return this;
    },
    sendProductListView ( items, itemList ) {
        googleAnalyticsLayer.productListView( items, itemList );
        return this;
    }
} );

export const search = dataLayer.create( {
    sendSearchResults ( detail ) {
        googleAnalyticsLayer.searchResults( detail );
        return this;
    },
    sendSearchSuggestionClick ( detail ) {
        googleAnalyticsLayer.searchSuggestionClick( detail );
        return this;
    }
} );

export const promotions = dataLayer.create( {
    sendPromoView ( promo ) {
        googleAnalyticsLayer.promoView( promo );
        return this;
    },
    sendPromoClick ( promo ) {
        googleAnalyticsLayer.promoClick( promo );
        return this;
    }
} );

export const user = dataLayer.create( {
    sendUserRegistrationProgress ( {step, viewCount} ) {
        let result = null;
        switch ( step ) {
            case RegistrationSteps.ACCOUNT_VERIFICATION_REQUIRED:
                result = 'pending';
                break;
            case RegistrationSteps.ACCOUNT_VERIFIED:
                result = 'verified';
                break;
            default:
                result = null;
        }
        googleAnalyticsLayer.registrationProgress( {
            viewCount,
            step: RegistrationStepsMap[step] || 'Unknown',
            result
        } );
        return this;
    },
    sendUserLogin () {
        googleAnalyticsLayer.userLogin();
        return this;
    }
} );

const pageView = dataLayer.create( {
    homepage () {
        facebookPixelLayer.viewHomepage();
    },
    async cart () {
        const {cart: cartData, cartItems} = await this.getCartData();
        const cartScope = cartData.orderGuid;
        const serverCartAdditions = getServerCartAdditionItems(
            cartItems,
            parseConfirmedServerCartAdditions()
        );

        if ( serverCartAdditions.length > 0 ) {
            const attributedAdditions = applyProductSelectionAttribution(
                serverCartAdditions,
                undefined,
                cartScope
            ).map( item => isCanonicalItemList( item.itemList )
                ? item
                : {...item, itemList: CommerceItemLists.DIRECT_CART_ADD} );
            rememberCartAttributions( attributedAdditions, undefined, cartScope );
            removeProductSelections( attributedAdditions, undefined, cartScope );
            cart.sendItemsAdded( attributedAdditions );
        }

        googleAnalyticsLayer.viewCart( enrichProductsWithCartAttribution( cartItems, undefined, cartScope ) );
        facebookPixelLayer.viewCart();
    }
} );

dataLayer.cart = cart;
dataLayer.checkout = checkout;
dataLayer.forms = forms;
dataLayer.product = product;
dataLayer.search = search;
dataLayer.promotions = promotions;
dataLayer.user = user;
dataLayer.pageView = pageView;
dataLayer.itemLists = CommerceItemLists;
dataLayer.searchTypes = SearchTypes;
dataLayer.suggestionTypes = SuggestionTypes;

export default dataLayer;
